import React, {useContext} from "react";
import {IKensuCalendarEventProto, KensuCalendarEventProto, space} from "../../../../provider_api";
import {EditableStringField} from "../../../../components/Editable/EditableStringField";
import {EditablePhoneInputField} from "../../../../components/Editable/EditablePhoneInputField";
import {PaymentStatusWidget} from "../../../../util/BookingUtil";
import {EditableContext} from "../../../../components/Editable/EditableContext";
import IApiBookingProto = space.kenko.proto.IApiBookingProto;
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import {findLocation, findPractitioner} from "./CalendarUtil";
import {formatPhoneNumber} from "react-phone-number-input";

/**
 * Each calendar event type has its own editor. This could be important down the road
 * since event types could get quite different! Think of private appointments vs public
 * classes (w/ lots of students) or even some kind of appointment with 2 teachers.
 *
 * Note that it's safe to assume that in these editors, the correct 'oneof' is already
 * selected in the request proto.
 *
 * This is useMemo'd because not doing so cause the thing to rerender, and apparently
 * it created different DOM widgets because all of the inputs lost focus immediately
 * after typing a letter.
 */
export const AppointmentEditor = () => {

  const {business, businessProfile} = useProviderProfile();
  const {workingCopy} = useContext<EditableContext<KensuCalendarEventProto>>(EditableContext);

  return <>

    <div className={'CalendarModalRow'}>

      <EditableStringField<IKensuCalendarEventProto>
          inputType={'text'}
          label={'Practitioner'}
          immutable={true}
          getter={instance => {
            const practitioner = findPractitioner(businessProfile?.practitioners,
                instance.appointment?.booking?.details?.service?.practitionerId as string)
            return practitioner?.firstName + ' ' + practitioner?.lastName;
          }}
          errorKey={'practitionerId'}
      />

      {workingCopy.appointment?.booking?.details?.service?.locationId &&
          <EditableStringField<IKensuCalendarEventProto>
              label={'Location'}
              inputType={'text'}
              immutable={true}
              errorKey={'location'}
              getter={instance => findLocation(businessProfile,
                  instance.appointment?.booking?.details?.service?.locationId)?.locationName}
          />}

      {!workingCopy.appointment?.booking?.details?.service?.locationId &&
          business?.businessSettings?.offersVirtualServices &&
          <EditableStringField<IKensuCalendarEventProto>
              label={'Virtual meeting link'}
              inputType={'text'}
              errorKey={'location'}
              getter={instance => instance.appointment?.booking?.details?.service?.virtualMeetingLink}
              setter={(event, value) => new KensuCalendarEventProto({
                ...event,
                appointment: {
                  ...event.appointment,
                  booking: {
                    ...event.appointment?.booking,
                    details: {
                      ...event.appointment?.booking?.details,
                      service: {
                        ...event.appointment?.booking?.details?.service,
                        virtualMeetingLink: value
                      }
                    }
                  }
                }
              })}
          />}
    </div>

    <div className={'CalendarModalRow'}>

      <EditableStringField<IKensuCalendarEventProto>
          label={'Service'}
          immutable={true}
          inputType={'text'}
          getter={instance => instance.appointment?.booking?.details?.service?.serviceName}
      />

      <div className={'Editable'}>
        <div className={"EditableLabel"}>Payment status</div>
        <PaymentStatusWidget booking={workingCopy.appointment?.booking as IApiBookingProto}/>
      </div>

    </div>

    <div className={'CalendarModalRow'}>

      <EditableStringField<IKensuCalendarEventProto>
          label={'Client name'}
          immutable={true}
          getter={instance => instance.appointment?.booking?.details?.clientName}
          inputType={'text'}
      />

      <EditablePhoneInputField<IKensuCalendarEventProto>
          label={'Client phone number'}
          immutable={true}
          getter={instance => {
            let clientPhone = instance.appointment?.booking?.details?.clientPhone;
            let clientPhoneVerified = instance.appointment?.booking?.details?.clientPhoneVerified;
            return clientPhone ? `${formatPhoneNumber(clientPhone)} (${clientPhoneVerified ? 'verified' : 'unverified'})` : '';
          }}
      />

      <EditableStringField<IKensuCalendarEventProto>
          label={'Client email'}
          immutable={true}
          getter={instance => `${instance.appointment?.booking?.details?.clientEmail} (${instance.appointment?.booking?.details?.clientEmailVerified ? 'verified' : 'unverified'})`}
          errorKey={'clientEmail'}
          inputType={'email'}
      />
    </div>
  </>
}
